import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api-client";
import { useAuth } from "../lib/auth-store";

export interface BroadcastState {
  streamId: string;
  status: string;
  generation: string;
  blocked: boolean;
  stopping: boolean;
  media: "present" | "absent" | "unavailable" | "unchecked";
}
export function useBroadcastControl(id?: string) {
  const canEdit = useAuth((s) =>
    ["OWNER", "ADMIN", "MEMBER"].includes(s.user?.role ?? ""),
  );
  const qc = useQueryClient();
  const queryKey = ["broadcast-control", id];
  const query = useQuery({
    queryKey,
    queryFn: async () =>
      (await api.get<BroadcastState>(`/me/streams/${id}/broadcast`)).data,
    enabled: !!id && canEdit,
    refetchInterval: 3000,
    staleTime: 0,
    retry: false,
  });
  const action = useMutation({
    mutationFn: async (input: "stop" | "resume" | { kind: "stop"; generation: string }) => {
      const kind = typeof input === "string" ? input : input.kind;
      if (!query.data || !id)
        throw new Error("Reload the broadcast status and try again.");
      return (
        await api.post<BroadcastState>(`/me/streams/${id}/broadcast/${kind}`, {
          generation: typeof input === "string" ? query.data.generation : input.generation,
        })
      ).data;
    },
    onSuccess: (data) => qc.setQueryData(queryKey, data),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey });
      void qc.invalidateQueries({ queryKey: ["stream", id] });
      void qc.invalidateQueries({ queryKey: ["my-streams"] });
    },
  });
  const error = action.error as {
    response?: { data?: { message?: string } };
    message?: string;
  } | null;
  return {
    query,
    action,
    canEdit,
    error: error?.response?.data?.message || error?.message || "",
  };
}
export type BroadcastControl = ReturnType<typeof useBroadcastControl>;
